import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
    Grid,
    List,
    ListItem,
    Divider,
    Button,
    Typography,
    Switch,
    FormControlLabel,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    IconButton,
    Stack,
    Box,
    CircularProgress,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    Menu,
    MenuItem,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import AddIcon from '@mui/icons-material/Add';
import FolderIcon from '@mui/icons-material/Folder';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import EditIcon from '@mui/icons-material/Edit';
import { useTranslation } from 'react-i18next';
import {
    getWarehouseFull,
    saveNewUser,
    removeUser,
    saveWarehouse,
    updateLowThreshold,
    updateStock,
    upsertInventoryFolder,
    updateFolderLink,
    removeProduct,
} from '../modules/warehousedata';
import ConfirmationDialog from '../common/confirmationdialog';
import ProductSelectorAutocomplete from '../products/productselectorautocomplete';
import { cloneDeep } from 'lodash';
import WarehouseInventoryItem from '../domain/warehouseinventoryitem';
import ObjectIdGenerator from '../modules/objectidgenerator';

const WarehouseView = ({ id, warehouse, expanded }) => {
    const dispatch = useDispatch();
    const { t } = useTranslation();

    const [openAddDialog, setOpenAddDialog] = useState(false);
    const [newUserName, setNewUserName] = useState('');
    const [generatedPin, setGeneratedPin] = useState('');
    const [userToRemove, setUserToRemove] = useState(null);
    const [openRemoveDialog, setOpenRemoveDialog] = useState(false);
    const [openThresholdDialog, setOpenThresholdDialog] = useState(false);
    const [editingItem, setEditingItem] = useState(null);
    const [newThreshold, setNewThreshold] = useState('');
    const [thresholdError, setThresholdError] = useState('');
    const [openQuantityDialog, setOpenQuantityDialog] = useState(false);
    const [editingQuantityItem, setEditingQuantityItem] = useState(null);
    const [newQuantity, setNewQuantity] = useState('');
    const [quantityError, setQuantityError] = useState('');
    const [openAddProductDialog, setOpenAddProductDialog] = useState(false);
    const [openAddFolderDialog, setOpenAddFolderDialog] = useState(false);
    const [newFolderName, setNewFolderName] = useState('');
    const [folderNameError, setFolderNameError] = useState('');
    const [expandedFolders, setExpandedFolders] = useState({});
    const [editingFolder, setEditingFolder] = useState(null);
    const [contextMenu, setContextMenu] = useState(null);
    const [contextMenuItem, setContextMenuItem] = useState(null);
    const [draggedItem, setDraggedItem] = useState(null);
    const [dropTarget, setDropTarget] = useState(null);
    const [productToRemove, setProductToRemove] = useState(null);
    const [openRemoveProductDialog, setOpenRemoveProductDialog] = useState(false);

    useEffect(() => {
        if (expanded && !warehouse) {
            dispatch(getWarehouseFull(id));
        }
    }, [expanded, warehouse, dispatch, id]);

    const generatePin = () => Math.floor(10000 + Math.random() * 90000).toString();

    const onAddNewUser = () => {
        setGeneratedPin(generatePin());
        setNewUserName('');
        setOpenAddDialog(true);
    };

    const handleCloseDialog = () => {
        setOpenAddDialog(false);
        setGeneratedPin('');
        setNewUserName('');
    };

    const handleCreateUser = async () => {
        if (!newUserName) return;

        const user = { name: newUserName, pinCode: generatedPin, warehouseId: warehouse.id };
        await saveNewUser(user)(dispatch);
        handleCloseDialog();
    };

    const handleCopyPin = async () => {
        try {
            await navigator.clipboard.writeText(generatedPin);
        } catch (e) {
            // ignore copy errors silently
        }
    };

    const toggleAllowOrderFrom = async (e) => {
        const newVal = e.target.checked;
        const updated = { ...warehouse, allowOrderFrom: newVal };
        const ok = await saveWarehouse(updated)(dispatch);
        if (!ok) {
        }
    };

    const toggleAllowOrderTo = async (e) => {
        const newVal = e.target.checked;
        const updated = { ...warehouse, allowOrderTo: newVal };
        const ok = await saveWarehouse(updated)(dispatch);
        if (!ok) {
        }
    };

    const handleRemoveUserClick = (user) => {
        setUserToRemove(user);
        setOpenRemoveDialog(true);
    };

    const handleConfirmRemoveUser = async () => {
        console.log(userToRemove);
        if (userToRemove) {
            await removeUser(userToRemove.clusterId, warehouse.id)(dispatch);
            setOpenRemoveDialog(false);
            setUserToRemove(null);
        }
    };

    const handleCancelRemoveUser = () => {
        setOpenRemoveDialog(false);
        setUserToRemove(null);
    };

    const handleThresholdClick = (item) => {
        setEditingItem(item);
        setNewThreshold(item.lowThreshold);
        setThresholdError('');
        setOpenThresholdDialog(true);
    };

    const handleRemoveProduct = (item) => {
        setProductToRemove(item);
        setOpenRemoveProductDialog(true);
    };

    const handleConfirmRemoveProduct = async () => {
        if (productToRemove) {
            await removeProduct(productToRemove)(dispatch);
            setOpenRemoveProductDialog(false);
            setProductToRemove(null);
        }
    };

    const handleCancelRemoveProduct = () => {
        setOpenRemoveProductDialog(false);
        setProductToRemove(null);
    };

    const handleCloseThresholdDialog = () => {
        setOpenThresholdDialog(false);
        setEditingItem(null);
        setNewThreshold('');
        setThresholdError('');
    };

    const handleThresholdChange = (e) => {
        const value = e.target.value;
        setNewThreshold(value);

        const numValue = parseFloat(value);
        if (value === '' || isNaN(numValue) || numValue <= 0) {
            setThresholdError(t('warehouse.thresholdMustBePositive'));
        } else {
            setThresholdError('');
        }
    };

    const handleSaveThreshold = async () => {
        const numValue = parseFloat(newThreshold);
        if (isNaN(numValue) || numValue <= 0) {
            setThresholdError(t('warehouse.thresholdMustBePositive'));
            return;
        }

        const updatedInventoryItem = warehouse.inventory.find((item) => item.itemId === editingItem.itemId);
        const newItem = cloneDeep(updatedInventoryItem);
        newItem.lowThreshold = numValue;
        await updateLowThreshold(newItem)(dispatch);
        handleCloseThresholdDialog();
    };

    const handleQuantityClick = (item) => {
        setEditingQuantityItem(item);
        setNewQuantity(item.netQuantity);
        setQuantityError('');
        setOpenQuantityDialog(true);
    };

    const handleCloseQuantityDialog = () => {
        setOpenQuantityDialog(false);
        setEditingQuantityItem(null);
        setNewQuantity('');
        setQuantityError('');
    };

    const handleQuantityChange = (e) => {
        const value = e.target.value;
        setNewQuantity(value);

        const numValue = parseFloat(value);
        if (value === '' || isNaN(numValue) || numValue < 0) {
            setQuantityError(t('warehouse.quantityMustBeNonNegative'));
        } else {
            setQuantityError('');
        }
    };

    const handleSaveQuantity = async () => {
        const numValue = parseFloat(newQuantity);
        if (isNaN(numValue) || numValue < 0) {
            setQuantityError(t('warehouse.quantityMustBeNonNegative'));
            return;
        }

        const updatedInventoryItem = warehouse.inventory.find((item) => item.itemId === editingQuantityItem.itemId);
        const newItem = cloneDeep(updatedInventoryItem);
        newItem.netQuantity = numValue;
        await updateStock(newItem)(dispatch);
        handleCloseQuantityDialog();
    };

    const handleAddProductClick = () => {
        setOpenAddProductDialog(true);
    };

    const handleCloseAddProductDialog = () => {
        setOpenAddProductDialog(false);
    };

    const handleProductSelected = async (productId, productData) => {
        const newInventoryItem = {
            itemId: productId,
            itemName: productData.name,
            netQuantity: 0,
            lowThreshold: 0,
            warehouseId: warehouse.id,
            itemType: productData.bundle ? WarehouseInventoryItem.TypeBundle() : WarehouseInventoryItem.TypeProduct(),
        };
        await updateStock(newInventoryItem)(dispatch);
        handleCloseAddProductDialog();
    };

    const handleAddFolderClick = () => {
        setEditingFolder(null);
        setNewFolderName('');
        setFolderNameError('');
        setOpenAddFolderDialog(true);
    };

    const handleCloseAddFolderDialog = () => {
        setOpenAddFolderDialog(false);
        setEditingFolder(null);
        setNewFolderName('');
        setFolderNameError('');
    };

    const handleFolderNameChange = (e) => {
        const value = e.target.value;
        setNewFolderName(value);

        if (!value || value.trim() === '') {
            setFolderNameError(t('warehouse.folderNameRequired'));
        } else {
            setFolderNameError('');
        }
    };

    const handleCreateFolder = async () => {
        if (!newFolderName || newFolderName.trim() === '') {
            setFolderNameError(t('warehouse.folderNameRequired'));
            return;
        }

        if (editingFolder) {
            // Rename existing folder
            const updatedFolder = cloneDeep(editingFolder);
            updatedFolder.itemName = newFolderName.trim();
            await upsertInventoryFolder(updatedFolder)(dispatch);
        } else {
            // Create new folder - always at top level (no parentFolderId)
            const newFolderItem = {
                itemId: await ObjectIdGenerator.newId(),
                itemName: newFolderName.trim(),
                netQuantity: 0,
                lowThreshold: 0,
                warehouseId: warehouse.id,
                itemType: WarehouseInventoryItem.TypeFolder(),
                parentFolderId: null, // Folders are always top-level
            };
            await upsertInventoryFolder(newFolderItem)(dispatch);
        }
        handleCloseAddFolderDialog();
    };

    const handleRenameFolderClick = (folder) => {
        setEditingFolder(folder);
        setNewFolderName(folder.itemName);
        setFolderNameError('');
        setOpenAddFolderDialog(true);
    };

    const handleContextMenu = (event, item) => {
        event.preventDefault();
        setContextMenu(
            contextMenu === null
                ? {
                      mouseX: event.clientX + 2,
                      mouseY: event.clientY - 6,
                  }
                : null
        );
        setContextMenuItem(item);
    };

    const handleCloseContextMenu = () => {
        setContextMenu(null);
        setContextMenuItem(null);
    };

    const getChildItems = (folderId) => {
        if (!warehouse || !warehouse.inventory) return [];
        return warehouse.inventory.filter((item) => item.parentFolderId === folderId);
    };

    const getTopLevelItems = () => {
        if (!warehouse || !warehouse.inventory) return [];
        return warehouse.inventory
            .filter((item) => !item.parentFolderId)
            .sort((a, b) => {
                const aIsFolder = a.itemType === WarehouseInventoryItem.TypeFolder();
                const bIsFolder = b.itemType === WarehouseInventoryItem.TypeFolder();
                if (aIsFolder && !bIsFolder) return -1;
                if (!aIsFolder && bIsFolder) return 1;
                return a.itemName.localeCompare(b.itemName);
            });
    };

    const toggleFolder = (folderId) => {
        setExpandedFolders((prev) => ({
            ...prev,
            [folderId]: !prev[folderId],
        }));
    };

    const handleDragStart = (e, item) => {
        setDraggedItem(item);
        e.dataTransfer.effectAllowed = 'move';
    };

    const handleDragEnd = () => {
        setDraggedItem(null);
        setDropTarget(null);
    };

    const handleDragOver = (e, targetFolderId) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        setDropTarget(targetFolderId);
    };

    const handleDragLeave = () => {
        setDropTarget(null);
    };

    const handleDrop = async (e, targetFolderId) => {
        e.preventDefault();
        if (!draggedItem) return;

        const updatedItem = cloneDeep(draggedItem);
        updatedItem.parentFolderId = targetFolderId;
        await updateFolderLink(updatedItem)(dispatch);

        setDraggedItem(null);
        setDropTarget(null);
    };

    if (!expanded) return null;

    if (!warehouse) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" p={2}>
                <CircularProgress size={28} />
            </Box>
        );
    }

    return (
        <List style={{ width: '100%' }}>
            {warehouse.customer && (
                <ListItem>
                    <Typography variant="body2">
                        <strong>{t('general.customer')}</strong>: {warehouse.customer.name}
                    </Typography>
                </ListItem>
            )}
            <ListItem>
                <Grid container spacing={1} alignItems="center" justifyContent="flex-start">
                    {Array.isArray(warehouse.users) && warehouse.users.length > 0 && (
                        <>
                            <Grid item xs={12}>
                                <Typography variant="body2">
                                    <strong>{t('warehouse.accessRights')}:</strong>
                                </Typography>
                            </Grid>

                            {warehouse.users.map((user) => (
                                <Grid
                                    key={user.name}
                                    item
                                    xs={12}
                                    sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <Box sx={{ textAlign: 'left' }}>{user.name}</Box>
                                    <Stack direction="row" spacing={1} alignItems="center">
                                        <Button
                                            variant="contained"
                                            color="primary"
                                            size="small"
                                            onClick={() => handleRemoveUserClick(user)}>
                                            {t('warehouse.removeAccess')}
                                        </Button>
                                    </Stack>
                                </Grid>
                            ))}
                        </>
                    )}

                    <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'flex-start' }}>
                        <Button
                            variant="contained"
                            size="small"
                            color="primary"
                            onClick={onAddNewUser}
                            startIcon={<AddIcon />}>
                            {t('warehouse.accessRight')}
                        </Button>
                    </Grid>

                    <Grid item xs={12}>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={!!warehouse.allowOrderFrom}
                                    onChange={toggleAllowOrderFrom}
                                    color={warehouse.allowOrderFrom ? 'primary' : 'default'}
                                    sx={{
                                        ...(warehouse.allowOrderFrom
                                            ? {}
                                            : {
                                                  '& .MuiSwitch-track': {
                                                      bgcolor: (theme) => theme.palette.action.disabledBackground,
                                                  },
                                                  '& .MuiSwitch-thumb': {
                                                      bgcolor: (theme) => theme.palette.action.disabled,
                                                  },
                                              }),
                                    }}
                                />
                            }
                            label={t('warehouse.orderFromWarehouse')}
                            sx={{ opacity: warehouse.allowOrderFrom ? 1 : 0.45 }}
                        />
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={!!warehouse.allowOrderTo}
                                    onChange={toggleAllowOrderTo}
                                    color={warehouse.allowOrderTo ? 'primary' : 'default'}
                                    sx={{
                                        ...(warehouse.allowOrderTo
                                            ? {}
                                            : {
                                                  '& .MuiSwitch-track': {
                                                      bgcolor: (theme) => theme.palette.action.disabledBackground,
                                                  },
                                                  '& .MuiSwitch-thumb': {
                                                      bgcolor: (theme) => theme.palette.action.disabled,
                                                  },
                                              }),
                                    }}
                                />
                            }
                            label={t('warehouse.orderToWarehouse')}
                            sx={{ opacity: warehouse.allowOrderTo ? 1 : 0.45 }}
                        />
                    </Grid>
                </Grid>
                <Dialog open={openAddDialog} onClose={handleCloseDialog}>
                    <DialogTitle>{t('warehouse.addAccess')}</DialogTitle>
                    <DialogContent>
                        <TextField
                            autoFocus
                            margin="dense"
                            label={t('general.name')}
                            fullWidth
                            value={newUserName}
                            onChange={(e) => setNewUserName(e.target.value)}
                        />
                        <TextField
                            margin="dense"
                            label={t('warehouse.hintPin')}
                            fullWidth
                            value={generatedPin}
                            InputProps={{
                                readOnly: true,
                                endAdornment: (
                                    <IconButton size="small" onClick={handleCopyPin} aria-label="copy pin">
                                        <ContentCopyIcon fontSize="small" />
                                    </IconButton>
                                ),
                            }}
                        />
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleCloseDialog}>{t('buttons.cancel')}</Button>
                        <Button onClick={handleCreateUser} disabled={!newUserName} variant="contained">
                            {t('buttons.add')}
                        </Button>
                    </DialogActions>
                </Dialog>
            </ListItem>
            <ConfirmationDialog
                open={openRemoveDialog}
                confirm={handleConfirmRemoveUser}
                cancel={handleCancelRemoveUser}
                confirmText="Poista">
                {userToRemove && (
                    <Typography>
                        {t('warehouse.confirmRemoveAccess')} <strong>{userToRemove.name}</strong>?
                    </Typography>
                )}
            </ConfirmationDialog>
            <ConfirmationDialog
                open={openRemoveProductDialog}
                confirm={handleConfirmRemoveProduct}
                cancel={handleCancelRemoveProduct}
                confirmText={t('buttons.remove')}>
                {productToRemove && (
                    <Typography>
                        {t('warehouse.confirmRemoveProduct')} <strong>{productToRemove.itemName}</strong>?
                    </Typography>
                )}
            </ConfirmationDialog>
            <Dialog open={openThresholdDialog} onClose={handleCloseThresholdDialog}>
                <DialogTitle>{t('warehouse.editLowThreshold')}</DialogTitle>
                <DialogContent>
                    {editingItem && (
                        <Typography variant="body2" sx={{ mb: 2 }}>
                            <strong>{editingItem.itemName}</strong>
                        </Typography>
                    )}
                    <TextField
                        autoFocus
                        margin="dense"
                        label={t('warehouse.lowThreshold')}
                        type="number"
                        fullWidth
                        value={newThreshold}
                        onChange={handleThresholdChange}
                        error={!!thresholdError}
                        helperText={thresholdError}
                        inputProps={{ min: 0, step: 1 }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseThresholdDialog}>{t('buttons.cancel')}</Button>
                    <Button
                        onClick={handleSaveThreshold}
                        disabled={!!thresholdError || !newThreshold}
                        variant="contained">
                        {t('buttons.save')}
                    </Button>
                </DialogActions>
            </Dialog>
            <Dialog open={openQuantityDialog} onClose={handleCloseQuantityDialog}>
                <DialogTitle>{t('warehouse.editQuantity')}</DialogTitle>
                <DialogContent>
                    {editingQuantityItem && (
                        <Typography variant="body2" sx={{ mb: 2 }}>
                            <strong>{editingQuantityItem.itemName}</strong>
                        </Typography>
                    )}
                    <TextField
                        autoFocus
                        margin="dense"
                        label={t('warehouse.stock')}
                        type="number"
                        fullWidth
                        value={newQuantity}
                        onChange={handleQuantityChange}
                        error={!!quantityError}
                        helperText={quantityError}
                        inputProps={{ min: 0, step: 1 }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseQuantityDialog}>{t('buttons.cancel')}</Button>
                    <Button
                        onClick={handleSaveQuantity}
                        disabled={!!quantityError || newQuantity === ''}
                        variant="contained">
                        {t('buttons.save')}
                    </Button>
                </DialogActions>
            </Dialog>
            <Divider />
            <h4>{t('general.products')}</h4>
            <Table size="small">
                <TableHead>
                    <TableRow>
                        <TableCell>{t('product.name')}</TableCell>
                        <TableCell>{t('warehouse.stock')}</TableCell>
                        <TableCell>{t('warehouse.lowThreshold')}</TableCell>
                        <TableCell></TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {getTopLevelItems().map((item) => {
                        const isFolder = item.itemType === WarehouseInventoryItem.TypeFolder();
                        const isBelowThreshold = item.netQuantity < item.lowThreshold;
                        const isExpanded = expandedFolders[item.itemId];

                        return (
                            <React.Fragment key={item.itemId}>
                                <TableRow
                                    draggable={!isFolder}
                                    onDragStart={(e) => !isFolder && handleDragStart(e, item)}
                                    onDragEnd={handleDragEnd}
                                    onDragOver={(e) => isFolder && handleDragOver(e, item.itemId)}
                                    onDragLeave={isFolder ? handleDragLeave : undefined}
                                    onDrop={(e) => isFolder && handleDrop(e, item.itemId)}
                                    onContextMenu={(e) => !isFolder && handleContextMenu(e, item)}
                                    sx={{
                                        backgroundColor: isFolder
                                            ? dropTarget === item.itemId
                                                ? 'primary.light'
                                                : 'action.hover'
                                            : isBelowThreshold
                                              ? 'warning.light'
                                              : 'inherit',
                                        '&:hover': {
                                            backgroundColor: isFolder
                                                ? 'action.selected'
                                                : isBelowThreshold
                                                  ? 'warning.main'
                                                  : 'action.hover',
                                        },
                                        cursor: isFolder ? 'default' : 'move',
                                        opacity: draggedItem && draggedItem.itemId === item.itemId ? 0.5 : 1,
                                    }}>
                                    <TableCell>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            {isFolder && (
                                                <IconButton
                                                    size="small"
                                                    onClick={() => toggleFolder(item.itemId)}
                                                    sx={{ padding: 0 }}>
                                                    {isExpanded ? (
                                                        <KeyboardArrowDownIcon />
                                                    ) : (
                                                        <KeyboardArrowRightIcon />
                                                    )}
                                                </IconButton>
                                            )}
                                            {isFolder && <FolderIcon fontSize="small" />}
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    fontWeight: isFolder ? 600 : 400,
                                                }}>
                                                {item.itemName}
                                            </Typography>
                                        </Box>
                                    </TableCell>
                                    <TableCell
                                        onClick={(e) => {
                                            if (isFolder) return;
                                            e.stopPropagation();
                                            handleQuantityClick(item);
                                        }}
                                        sx={{ cursor: isFolder ? 'default' : 'pointer' }}>
                                        {!isFolder && item.netQuantity}
                                    </TableCell>
                                    <TableCell
                                        onClick={(e) => {
                                            if (isFolder) return;
                                            e.stopPropagation();
                                            handleThresholdClick(item);
                                        }}
                                        sx={{ cursor: isFolder ? 'default' : 'pointer' }}>
                                        {!isFolder && item.lowThreshold}
                                    </TableCell>
                                    <TableCell>
                                        {isFolder && (
                                            <IconButton
                                                size="small"
                                                onClick={() => handleRenameFolderClick(item)}
                                                sx={{ padding: 0.5 }}>
                                                <EditIcon fontSize="small" />
                                            </IconButton>
                                        )}
                                    </TableCell>
                                </TableRow>
                                {isFolder &&
                                    isExpanded &&
                                    getChildItems(item.itemId).map((subItem) => {
                                        const subIsBelowThreshold = subItem.netQuantity < subItem.lowThreshold;
                                        return (
                                            <TableRow
                                                key={subItem.itemId}
                                                draggable
                                                onDragStart={(e) => handleDragStart(e, subItem)}
                                                onDragEnd={handleDragEnd}
                                                onContextMenu={(e) => handleContextMenu(e, subItem)}
                                                sx={{
                                                    backgroundColor: subIsBelowThreshold ? 'warning.light' : 'inherit',
                                                    '&:hover': {
                                                        backgroundColor: subIsBelowThreshold
                                                            ? 'warning.main'
                                                            : 'action.hover',
                                                    },
                                                    cursor: 'move',
                                                    opacity:
                                                        draggedItem && draggedItem.itemId === subItem.itemId ? 0.5 : 1,
                                                }}>
                                                <TableCell sx={{ pl: 8 }}>
                                                    <Typography variant="body2">{subItem.itemName}</Typography>
                                                </TableCell>
                                                <TableCell
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleQuantityClick(subItem);
                                                    }}
                                                    sx={{ cursor: 'pointer' }}>
                                                    {subItem.netQuantity}
                                                </TableCell>
                                                <TableCell
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleThresholdClick(subItem);
                                                    }}
                                                    sx={{ cursor: 'pointer' }}>
                                                    {subItem.lowThreshold}
                                                </TableCell>
                                                <TableCell></TableCell>
                                            </TableRow>
                                        );
                                    })}
                            </React.Fragment>
                        );
                    })}
                </TableBody>
            </Table>
            <br />
            <Stack direction="row" spacing={2}>
                <Button variant="contained" color="primary" onClick={handleAddProductClick} startIcon={<AddIcon />}>
                    {t('general.product')}
                </Button>
                <Button variant="contained" color="primary" onClick={handleAddFolderClick} startIcon={<FolderIcon />}>
                    {t('general.folder')}
                </Button>
            </Stack>
            <Dialog open={openAddProductDialog} onClose={handleCloseAddProductDialog} maxWidth="md" fullWidth>
                <DialogTitle>{t('warehouse.addProduct')}</DialogTitle>
                <DialogContent>
                    <ProductSelectorAutocomplete
                        showAllProducts={true}
                        selected={handleProductSelected}
                        dismiss={handleCloseAddProductDialog}
                        showBundles={true}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseAddProductDialog}>{t('buttons.cancel')}</Button>
                </DialogActions>
            </Dialog>
            <Dialog open={openAddFolderDialog} onClose={handleCloseAddFolderDialog} maxWidth="md" fullWidth>
                <DialogTitle>{editingFolder ? t('warehouse.renameFolder') : t('warehouse.addFolder')}</DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        margin="dense"
                        label={t('general.name')}
                        fullWidth
                        value={newFolderName}
                        onChange={handleFolderNameChange}
                        error={!!folderNameError}
                        helperText={folderNameError}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseAddFolderDialog}>{t('buttons.cancel')}</Button>
                    <Button
                        onClick={handleCreateFolder}
                        disabled={!!folderNameError || !newFolderName}
                        variant="contained">
                        {editingFolder ? t('buttons.save') : t('buttons.create')}
                    </Button>
                </DialogActions>
            </Dialog>
            <Menu
                open={contextMenu !== null}
                onClose={handleCloseContextMenu}
                anchorReference="anchorPosition"
                anchorPosition={
                    contextMenu !== null ? { top: contextMenu.mouseY, left: contextMenu.mouseX } : undefined
                }>
                <MenuItem
                    onClick={() => {
                        handleCloseContextMenu();
                        handleQuantityClick(contextMenuItem);
                    }}>
                    {t('warehouse.editQuantity')}
                </MenuItem>
                <MenuItem
                    onClick={() => {
                        handleCloseContextMenu();
                        handleThresholdClick(contextMenuItem);
                    }}>
                    {t('warehouse.editLowThreshold')}
                </MenuItem>

                <MenuItem
                    onClick={() => {
                        handleCloseContextMenu();
                        handleRemoveProduct(contextMenuItem);
                    }}>
                    {t('warehouse.removeProduct')}
                </MenuItem>
            </Menu>
        </List>
    );
};

export default WarehouseView;
